import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import ModalShell from '../modals/ModalShell';
import { Button } from './Button';
import './ConfirmDialog.css';

/**
 * App-wide confirmation / prompt dialogs, in place of the browser's native
 * `window.confirm` / `window.prompt`.
 *
 * The native dialogs are unstyled, block the main thread, ignore the theme, and cannot
 * carry the destructive-action affordance (a red button) the design system uses
 * everywhere else. This provider renders a real, accessible dialog (via `ModalShell`:
 * focus trap, focus restore, Escape, scroll lock, `role="dialog"`) and exposes the same
 * imperative shape callers already expect — `if (!(await confirm(...))) return;` — so a
 * `window.confirm` site becomes a one-line change.
 */
export interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (red) — for deletes and removals. */
  danger?: boolean;
}

export interface PromptOptions extends ConfirmOptions {
  placeholder?: string;
  defaultValue?: string;
  /** Disable confirm until the field is non-empty. */
  required?: boolean;
  multiline?: boolean;
}

export interface ConfirmApi {
  /** Resolves true if confirmed, false if cancelled/dismissed. */
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  /** Resolves the entered text if confirmed, null if cancelled/dismissed. */
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

type Pending =
  | { mode: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { mode: 'prompt'; opts: PromptOptions; resolve: (v: string | null) => void };

const ConfirmContext = createContext<ConfirmApi | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [value, setValue] = useState('');

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setValue('');
        setPending({ mode: 'confirm', opts, resolve });
      }),
    [],
  );

  const prompt = useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        setValue(opts.defaultValue ?? '');
        setPending({ mode: 'prompt', opts, resolve });
      }),
    [],
  );

  const api = useMemo(() => ({ confirm, prompt }), [confirm, prompt]);

  const handleCancel = useCallback(() => {
    setPending((cur) => {
      if (cur) cur.mode === 'confirm' ? cur.resolve(false) : cur.resolve(null);
      return null;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setPending((cur) => {
      if (!cur) return null;
      if (cur.mode === 'prompt') {
        if (cur.opts.required && !value.trim()) return cur; // keep open until filled
        cur.resolve(value);
      } else {
        cur.resolve(true);
      }
      return null;
    });
  }, [value]);

  const confirmDisabled = pending?.mode === 'prompt' && !!pending.opts.required && !value.trim();

  return (
    <ConfirmContext.Provider value={api}>
      {children}
      {pending && (
        <ModalShell onClose={handleCancel} title={pending.opts.title} panelClassName="modal-sm confirm-dialog">
          <form
            className="confirm-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
          >
            <div className="confirm-body">
              <h2 className="modal-title confirm-title">{pending.opts.title}</h2>
              {pending.opts.message && <p className="confirm-message">{pending.opts.message}</p>}
              {pending.mode === 'prompt' &&
                (pending.opts.multiline ? (
                  <textarea
                    className="confirm-input"
                    autoFocus
                    rows={3}
                    value={value}
                    placeholder={pending.opts.placeholder}
                    aria-label={pending.opts.title}
                    onChange={(e) => setValue(e.target.value)}
                  />
                ) : (
                  <input
                    className="confirm-input"
                    autoFocus
                    value={value}
                    placeholder={pending.opts.placeholder}
                    aria-label={pending.opts.title}
                    onChange={(e) => setValue(e.target.value)}
                  />
                ))}
            </div>
            <div className="confirm-actions">
              <Button type="button" variant="secondary" onClick={handleCancel}>
                {pending.opts.cancelLabel ?? 'Cancel'}
              </Button>
              <Button type="submit" variant={pending.opts.danger ? 'danger' : 'primary'} disabled={confirmDisabled}>
                {pending.opts.confirmLabel ?? 'Confirm'}
              </Button>
            </div>
          </form>
        </ModalShell>
      )}
    </ConfirmContext.Provider>
  );
}

// Degrade to the native dialogs rather than silently no-op when no provider is mounted:
// a confirm that always returned false would block every delete; one that always returned
// true would be dangerous. Native is the safe fallback (e.g. in isolated unit tests).
const FALLBACK: ConfirmApi = {
  confirm: async (o) =>
    typeof window !== 'undefined'
      ? window.confirm(typeof o.message === 'string' ? `${o.title}\n\n${o.message}` : o.title)
      : false,
  prompt: async (o) =>
    typeof window !== 'undefined' ? window.prompt(o.title, o.defaultValue ?? '') : null,
};

export function useConfirm(): ConfirmApi {
  return useContext(ConfirmContext) ?? FALLBACK;
}
